import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDStore } from '@/stores/useDStore'
import { formatDistanceToNow } from 'date-fns'
import { FileText, Loader2, ChevronLeft } from 'lucide-react'
import type { FoolscapSummary, DrafterSpec } from '@/lib/api'

// =============================================================================
// Types
// =============================================================================

export type PieceType = DrafterSpec['piece_type']

const PIECE_TYPE_OPTIONS: { value: PieceType; label: string }[] = [
  { value: 'essay', label: 'Essay' },
  { value: 'article', label: 'Article' },
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'thread', label: 'Thread' },
  { value: 'script', label: 'Script' },
  { value: 'memo', label: 'Memo' },
  { value: 'letter', label: 'Letter' },
  { value: 'deck', label: 'Deck' },
]

const LENGTH_PRESETS = [
  { value: 500, label: '~500 words' },
  { value: 1000, label: '~1000 words' },
  { value: 2000, label: '~2000 words' },
  { value: 3000, label: '~3000 words' },
]

// =============================================================================
// Component
// =============================================================================

/**
 * DFoolscapPicker - Modal for selecting a completed Foolscap and configuring drafter spec.
 *
 * Two-step flow:
 * 1. Select a Foolscap from the list
 * 2. Configure piece type and target length
 * 3. Create D session with the spec
 */
export function DFoolscapPicker() {
  const {
    showFoolscapPicker,
    closeFoolscapPicker,
    availableFoolscaps,
    isLoadingFoolscaps,
    fetchAvailableFoolscaps,
    createSessionFromFoolscap,
  } = useDStore()

  // Two-step state
  const [selectedFoolscap, setSelectedFoolscap] = useState<FoolscapSummary | null>(null)
  const [pieceType, setPieceType] = useState<PieceType>('essay')
  const [targetLength, setTargetLength] = useState<number>(1000)
  const [customLength, setCustomLength] = useState<string>('')
  const [useCustomLength, setUseCustomLength] = useState(false)
  const [isCreating, setIsCreating] = useState(false)

  useEffect(() => {
    if (showFoolscapPicker) {
      fetchAvailableFoolscaps()
      // Reset state when opening
      setSelectedFoolscap(null)
      setPieceType('essay')
      setTargetLength(1000)
      setCustomLength('')
      setUseCustomLength(false)
    }
  }, [showFoolscapPicker, fetchAvailableFoolscaps])

  if (!showFoolscapPicker) return null

  const handleSelectFoolscap = (foolscap: FoolscapSummary) => {
    setSelectedFoolscap(foolscap)
  }

  const handleBack = () => {
    setSelectedFoolscap(null)
  }

  const handleCreate = async () => {
    if (!selectedFoolscap) return

    setIsCreating(true)
    try {
      const spec: DrafterSpec = {
        piece_type: pieceType,
        target_length: useCustomLength ? parseInt(customLength, 10) || 1000 : targetLength,
      }
      await createSessionFromFoolscap(
        selectedFoolscap.session_id,
        selectedFoolscap.name || selectedFoolscap.entity_name || undefined,
        spec
      )
    } finally {
      setIsCreating(false)
    }
  }

  const handleLengthPresetClick = (value: number) => {
    setTargetLength(value)
    setUseCustomLength(false)
  }

  const handleCustomLengthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomLength(e.target.value)
    setUseCustomLength(true)
  }

  const isValidCustomLength = !useCustomLength || (parseInt(customLength, 10) > 0)

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-foreground/10 backdrop-blur-sm"
        onClick={closeFoolscapPicker}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md mx-4 max-h-[80vh] bg-background border border-border rounded-lg overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-5 border-b border-border">
          {selectedFoolscap ? (
            <div className="flex items-center gap-3">
              <button
                onClick={handleBack}
                className="p-1 -ml-1 text-muted-foreground hover:text-foreground transition-colors"
              >
                <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
              </button>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-light text-foreground truncate">
                  {selectedFoolscap.name || selectedFoolscap.entity_name || 'Untitled'}
                </h2>
                <p className="text-sm font-light text-muted-foreground">
                  Configure your draft
                </p>
              </div>
            </div>
          ) : (
            <>
              <h2 className="text-lg font-light text-foreground">
                Select a Foolscap
              </h2>
              <p className="mt-1 text-sm font-light text-muted-foreground">
                Choose a completed Foolscap to draft.
              </p>
            </>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain p-4">
          {selectedFoolscap ? (
            // Step 2: Drafter Spec Form
            <div className="space-y-6">
              {/* Piece Type */}
              <div>
                <label className="block text-[10px] font-light uppercase tracking-[0.08em] text-muted-foreground mb-3">
                  Piece Type
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {PIECE_TYPE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setPieceType(option.value)}
                      className={`
                        px-3 py-2.5 text-sm font-light rounded-md border transition-colors
                        ${pieceType === option.value
                          ? 'border-foreground bg-foreground text-background'
                          : 'border-border hover:border-foreground/30 hover:bg-muted/50 text-foreground'
                        }
                      `}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Target Length */}
              <div>
                <label className="block text-[10px] font-light uppercase tracking-[0.08em] text-muted-foreground mb-3">
                  Target Length
                </label>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {LENGTH_PRESETS.map((preset) => (
                    <button
                      key={preset.value}
                      type="button"
                      onClick={() => handleLengthPresetClick(preset.value)}
                      className={`
                        px-3 py-2.5 text-sm font-light rounded-md border transition-colors
                        ${targetLength === preset.value && !useCustomLength
                          ? 'border-foreground bg-foreground text-background'
                          : 'border-border hover:border-foreground/30 hover:bg-muted/50 text-foreground'
                        }
                      `}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={customLength}
                    onChange={handleCustomLengthChange}
                    placeholder="Custom"
                    min="100"
                    max="50000"
                    className={`
                      flex-1 px-3 py-2.5 text-sm font-light bg-transparent border rounded-md
                      placeholder:text-muted-foreground
                      focus:outline-none focus:border-foreground/30
                      ${useCustomLength ? 'border-foreground/50' : 'border-border'}
                    `}
                  />
                  <span className="text-sm font-light text-muted-foreground">words</span>
                </div>
              </div>
            </div>
          ) : (
            // Step 1: Foolscap List
            <>
              {isLoadingFoolscaps ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : availableFoolscaps.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-8 h-8 mx-auto mb-3 text-muted-foreground/50" strokeWidth={1.5} />
                  <h3 className="text-sm font-light text-foreground mb-1">
                    No completed Foolscaps
                  </h3>
                  <p className="text-sm font-light text-muted-foreground">
                    Complete a Foolscap in /f first.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {availableFoolscaps.map((foolscap) => (
                    <button
                      key={foolscap.session_id}
                      onClick={() => handleSelectFoolscap(foolscap)}
                      className="w-full text-left p-4 rounded-md border border-border hover:border-foreground/30 hover:bg-muted/50 transition-colors group"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-light text-foreground truncate group-hover:text-foreground">
                            {foolscap.name || foolscap.entity_name || 'Untitled Foolscap'}
                          </h3>
                          {foolscap.foolscap_markdown && (
                            <p className="mt-1 text-xs font-light text-muted-foreground line-clamp-2">
                              {foolscap.foolscap_markdown.slice(0, 120)}...
                            </p>
                          )}
                        </div>
                        <div className="flex-shrink-0 text-[10px] font-light uppercase tracking-[0.08em] text-muted-foreground whitespace-nowrap">
                          {formatDistanceToNow(new Date(foolscap.updated_at), { addSuffix: true })}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-border">
          <div className="flex justify-end gap-2">
            <button
              onClick={closeFoolscapPicker}
              className="px-4 py-2.5 text-sm font-light text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
            {selectedFoolscap && (
              <button
                onClick={handleCreate}
                disabled={isCreating || !isValidCustomLength}
                className="px-4 py-2.5 text-sm font-light bg-foreground text-background rounded-md hover:bg-foreground/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </span>
                ) : (
                  'Start Draft'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
